import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { programById, programDay } from '@erectus/shared';
import type { ProgramWeek } from '@erectus/shared';
import { Button, Card, ErrorState } from '@/ui/primitives.js';
import { PageHeader } from '@/ui/Layout.js';
import { Icon } from '@/ui/icons.js';
import { useAuth } from '@/stores/auth.js';
import { saveEnrollment } from '@/lib/repo.js';
import { track } from '@/lib/analytics.js';
import { ProgramPicker } from './ProgramPicker.js';
import { SnackCard } from './SnackCard.js';
import { TodayPanel } from './TodayPanel.js';
import { useProgramToday } from './useProgram.js';

/**
 * Plan (docs/PRODUCT.md §12): the 4- or 6-week program that ties the app's
 * parts together — a camera check each week, rest breaks through the working
 * day, exercise snacks — and says why each week looks the way it does.
 * Without a program: the picker, and the exercise library.
 */
export function ProgramPage(): ReactNode {
  const { user, userDoc, refreshUserDoc } = useAuth();
  const today = useProgramToday();
  const enrollment = userDoc?.program && !userDoc.program.endedAt ? userDoc.program : null;
  const program = enrollment ? programById(enrollment.programId) ?? null : null;
  const [choice, setChoice] = useState<string | null>(null);
  const [confirmStop, setConfirmStop] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async (programId: string): Promise<void> => {
    if (!user) return;
    setError(null);
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    try {
      await saveEnrollment(user.uid, { programId, startedAt: midnight.getTime(), endedAt: null });
      track('program_start', { program_id: programId, from: 'program' });
      await refreshUserDoc();
    } catch {
      setError('Could not start the program. Check your connection and try again.');
    }
  };

  const stop = async (): Promise<void> => {
    if (!user || !enrollment || !program) return;
    const at = programDay(enrollment, Date.now());
    try {
      await saveEnrollment(user.uid, { ...enrollment, endedAt: Date.now() });
      track('program_stop', { program_id: program.id, week: Math.min(6, at?.week ?? 1) as 1 | 2 | 3 | 4 | 5 | 6 });
      setConfirmStop(false);
      await refreshUserDoc();
    } catch {
      setError('Could not stop the program. Check your connection and try again.');
    }
  };

  if (!program || !enrollment) {
    return (
      <div className="space-y-4">
        <PageHeader title="Plan" subtitle="A 4- or 6-week program: weekly checks, breaks and exercise snacks." />
        {error ? <ErrorState title="Not saved" message={error} /> : null}
        <Card className="space-y-2">
          <p className="text-sm text-ink-soft">
            A program puts the pieces together: a short camera check once a week to see how your sitting changes,
            rest breaks at varied times through your working day, and 2–5 minute exercise snacks. Each week says
            what it is for and what to look for in your reports.
          </p>
        </Card>
        <ProgramPicker value={choice} onChange={setChoice} allowNone={false} />
        <Button className="w-full" disabled={!choice} onClick={() => choice && void start(choice)}>
          Start {choice ? programById(choice)?.name : 'a program'}
        </Button>
        <LibraryLink />
      </div>
    );
  }

  const at = programDay(enrollment, Date.now());
  const currentWeek = today?.week ?? program.plan[(at?.week ?? 1) - 1]!;

  return (
    <div className="space-y-4">
      <PageHeader
        title={program.name}
        subtitle={at?.finished ? 'Program complete' : `Week ${at?.week ?? 1} of ${program.weeks} · day ${at?.day ?? 1}`}
      />
      {error ? <ErrorState title="Not saved" message={error} /> : null}

      {at?.finished ? (
        <Card className="space-y-3">
          <p className="font-semibold">You finished {program.name}.</p>
          <p className="text-sm text-ink-soft">
            Compare your final check with your first one in Progress. Keep the breaks going, or start another program.
          </p>
          <Button variant="secondary" onClick={() => void stop()}>
            Choose what’s next
          </Button>
        </Card>
      ) : (
        <TodayPanel today={today} />
      )}

      <WeekCard week={currentWeek} current />

      <Card className="space-y-2">
        <h2 className="font-semibold">All weeks</h2>
        <div className="divide-y divide-line">
          {program.plan.map((week) => (
            <details key={week.week} className="group py-2" open={false}>
              <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-sm [&::-webkit-details-marker]:hidden">
                <span>
                  <span className="font-semibold">Week {week.week}</span> · {week.theme}
                  {week.week === currentWeek.week ? <span className="ml-2 text-xs text-muted">this week</span> : null}
                </span>
                <Icon name="plus" size={16} className="shrink-0 text-muted transition group-open:rotate-45" />
              </summary>
              <div className="pt-3">
                <WeekBody week={week} />
              </div>
            </details>
          ))}
        </div>
      </Card>

      <Card className="space-y-2">
        <h2 className="font-semibold">Why it is built this way</h2>
        <p className="text-sm text-ink-soft">{program.rationale}</p>
        <Link to="/sources#breaks" className="text-sm font-semibold underline">
          Where the numbers come from
        </Link>
      </Card>

      <LibraryLink />

      <Card className="space-y-2">
        {confirmStop ? (
          <>
            <p className="text-sm">Stop {program.name}? Your logged breaks, snacks and sessions stay.</p>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setConfirmStop(false)}>
                Keep going
              </Button>
              <Button className="flex-1" onClick={() => void stop()}>
                Stop the program
              </Button>
            </div>
          </>
        ) : (
          <button className="text-sm text-muted underline" onClick={() => setConfirmStop(true)}>
            Stop or switch program
          </button>
        )}
      </Card>
    </div>
  );
}

function LibraryLink(): ReactNode {
  return (
    <Link to="/exercises" state={{ from: 'plan' }} className="flex items-center justify-between rounded-2xl border border-line bg-white p-4">
      <span>
        <span className="block font-semibold">All exercise snacks</span>
        <span className="block text-xs text-muted">Every move, with steps and what to watch for.</span>
      </span>
      <Icon name="back" size={16} className="rotate-180 text-muted" />
    </Link>
  );
}

const DAY_NAMES = ['Day 1', 'Day 2', 'Day 3', 'Day 4', 'Day 5', 'Day 6', 'Day 7'];

function WeekCard({ week, current = false }: { week: ProgramWeek; current?: boolean }): ReactNode {
  return (
    <Card className="space-y-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted">{current ? 'This week' : `Week ${week.week}`}</p>
        <h2 className="text-lg font-bold">
          Week {week.week}: {week.theme}
        </h2>
        <p className="text-sm text-ink-soft">{week.goal}</p>
      </div>
      <WeekBody week={week} />
    </Card>
  );
}

function WeekBody({ week }: { week: ProgramWeek }): ReactNode {
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-surface p-3 text-sm">
        <p className="font-semibold">Posture cue</p>
        <p className="text-ink-soft">{week.postureCue}</p>
      </div>
      <WeekStrip week={week} />
      <dl className="space-y-2 text-sm">
        <div>
          <dt className="font-semibold">Why</dt>
          <dd className="text-ink-soft">{week.why}</dd>
        </div>
        <div>
          <dt className="font-semibold">Breaks</dt>
          <dd className="text-ink-soft">
            Every {week.breaks.everyMin} min, about {week.breaks.minutes} min. {week.breaks.note}
          </dd>
        </div>
        <div>
          <dt className="font-semibold">What to look for</dt>
          <dd className="text-ink-soft">{week.whatToNotice}</dd>
        </div>
      </dl>
      <div className="space-y-2">
        <p className="text-sm font-semibold">This week’s snacks</p>
        {week.snacks.map((snack) => (
          <SnackCard key={snack.id} snack={snack} done={false} />
        ))}
      </div>
    </div>
  );
}

/** Seven days at a glance: camera check, snacks, rest. */
function WeekStrip({ week }: { week: ProgramWeek }): ReactNode {
  return (
    <ol className="grid grid-cols-7 gap-1" aria-label={`Week ${week.week} days`}>
      {week.days.map((d) => (
        <li
          key={d.day}
          className={`flex flex-col items-center gap-1 rounded-lg border px-0.5 py-1.5 text-[11px] ${
            d.rest ? 'border-dashed border-line text-muted' : 'border-line bg-white'
          }`}
          aria-label={`${DAY_NAMES[d.day - 1]}: ${[d.check ? 'camera check' : null, d.rest ? 'rest day' : `${d.snackIds.length} snacks`]
            .filter(Boolean)
            .join(', ')}`}
        >
          <span className="font-semibold">{d.day}</span>
          {d.check ? <Icon name="session" size={14} /> : <span className="size-3.5" aria-hidden />}
          <span>{d.rest ? 'rest' : `${d.snackIds.length}×`}</span>
        </li>
      ))}
    </ol>
  );
}

